import { useState } from "react";
import { X, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";

const CONF = { High: "var(--emerald)", Medium: "var(--amber)", Low: "#f87171" };

export default function ImportDemosDialog({ open, onClose, onAdded }) {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [picked, setPicked] = useState({});
  const [adding, setAdding] = useState(false);

  if (!open) return null;

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.gmailImportScan();
      setResult(r);
      setPicked(Object.fromEntries(r.candidates.map((c) => [c.email, !c.personalDomain])));
      if (!r.candidates.length) toast.info(`Scanned ${r.messages_scanned} emails — every label found is already in your list`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gmail scan failed"); }
    finally { setScanning(false); }
  };

  const add = async () => {
    const chosen = (result?.candidates || []).filter((c) => picked[c.email]);
    if (!chosen.length) return;
    setAdding(true);
    try {
      const r = await api.gmailImportConfirm(chosen);
      toast.success(`${r.added.length} contact${r.added.length === 1 ? "" : "s"} added to ADE Outreach`);
      onAdded?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setAdding(false); }
  };

  const edit = (email, key, value) => setResult((r) => ({ ...r, candidates: r.candidates.map((c) => (c.email === email ? { ...c, [key]: value } : c)) }));
  const count = Object.values(picked).filter(Boolean).length;

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" style={{ width: "min(960px, 100vw)" }} data-testid="import-demos-dialog">
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow">Gmail · demo history</div>
              <h2 className="text-2xl font-head mt-1">Import demo contacts</h2>
              <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>
                Scans your sent mail and inbox for demo / submission threads (last 3 years), lists the labels and agencies not yet in the tracker,
                and pre-fills each with AI-suggested details (company, role, fit, city, ADE presence). Review, edit, untick anything off — then add.
              </p>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="import-demos-close"><X size={14} /></button>
          </div>

          {!result && (
            <button className="btn btn-amber" onClick={scan} disabled={scanning} data-testid="import-demos-scan-button">
              <Search size={14} /> {scanning ? "Scanning Gmail & researching labels… (30–90s)" : "Scan my Gmail"}
            </button>
          )}

          {result && (
            <>
              <div className="flex flex-wrap items-center gap-3 mono text-[11px]" style={{ color: "var(--text-2)" }}>
                <span>{result.messages_scanned} emails scanned</span>
                <span>· {result.candidates.length} new contacts found</span>
                <span>· {count} selected</span>
                <button className="tab ml-auto" onClick={scan} disabled={scanning}>Rescan</button>
              </div>
              <div className="space-y-2" data-testid="import-demos-list">
                {result.candidates.map((c) => (
                  <div key={c.email} className="card p-3" style={{ background: "var(--surface-2)", opacity: picked[c.email] ? 1 : 0.55 }} data-testid={`import-candidate-${c.email}`}>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" className="check mt-1" checked={!!picked[c.email]} onChange={(e) => setPicked((p) => ({ ...p, [c.email]: e.target.checked }))} data-testid={`import-pick-${c.email}`} />
                      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input className="input" value={c.targetName} onChange={(e) => edit(c.email, "targetName", e.target.value)} placeholder="Contact / team name" />
                        <input className="input" value={c.company} onChange={(e) => edit(c.email, "company", e.target.value)} placeholder="Label / agency" />
                        <input className="input" value={c.role} onChange={(e) => edit(c.email, "role", e.target.value)} placeholder="Role" />
                        <div className="flex gap-2">
                          <select className="select" value={c.tab} onChange={(e) => edit(c.email, "tab", e.target.value)}>
                            <option value="labels">Labels</option><option value="management">Management / Agencies</option>
                          </select>
                          <select className="select" value={c.priority} onChange={(e) => edit(c.email, "priority", e.target.value)}>
                            <option>High</option><option>Medium</option><option>Low</option>
                          </select>
                        </div>
                        <input className="input md:col-span-2" value={c.fitSentence} onChange={(e) => edit(c.email, "fitSentence", e.target.value)} placeholder="…sits naturally in the lane of (fit phrase)" />
                        <textarea className="textarea md:col-span-2" style={{ minHeight: 56 }} value={c.soundFitNotes} onChange={(e) => edit(c.email, "soundFitNotes", e.target.value)} placeholder="Sound-fit notes" />
                        <div className="md:col-span-2 flex flex-wrap gap-x-4 gap-y-1 mono text-[11px]" style={{ color: "var(--text-2)" }}>
                          <span>{c.email}</span>
                          <span>{c.sentByMe} sent · {c.repliedToMe} received · last {c.lastDate}</span>
                          <span style={{ color: CONF[c.confidence] || "var(--text-2)" }}>AI confidence: {c.confidence} — verify</span>
                          {c.personalDomain && <span style={{ color: "var(--amber)" }}>personal mailbox domain</span>}
                          {c.cityNotes && <span>{c.cityNotes}</span>}
                        </div>
                        {c.subjects?.length > 0 && <div className="md:col-span-2 text-xs truncate" style={{ color: "var(--muted)" }} title={c.subjects.join(" · ")}>Threads: {c.subjects.slice(0, 3).join(" · ")}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button className="btn" onClick={onClose}>Cancel</button>
                <button className="btn btn-amber" onClick={add} disabled={adding || !count} data-testid="import-demos-add-button">
                  <Plus size={14} /> {adding ? "Adding…" : `Add ${count} to ADE Outreach`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
